/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { HelpDesk, ManagedSolutions } from '@carbon/react/icons';
import cx from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';

import { isCpd, isWxoEnabled } from '@ibm-watson/shared/modules/ia/util/config';
import { useAppSelector } from '../../../store';
import { PlanName } from '../../../ts/plan';
import {
  isFeatureEnabled,
  selectIsGenesysAudioConnectorInCpd
} from '../../ia/store/features/selectors';
import { AmazonConnectIcon } from '../components/icons/AmazonConnectIcon';
import { AmazonConnectIconMinimal } from '../components/icons/AmazonConnectIconMinimal';
import { CustomSMSIcon } from '../components/icons/CustomSMSIcon';
import { GenesysIcon } from '../components/icons/GenesysIcon';
import { GenesysIconMinimal } from '../components/icons/GenesysIconMinimal';
import { IntelepeerIcon } from '../components/icons/IntelepeerIcon';
import { IntelepeerIconMinimal } from '../components/icons/IntelepeerIconMinimal';
import { NiceInContactIcon } from '../components/icons/NiceInContactIcon';
import { NiceInContactIconMinimal } from '../components/icons/NiceInContactIconMinimal';
import { SalesforceIcon } from '../components/icons/SalesforceIcon';
import { TwilioIcon } from '../components/icons/TwilioIcon';
import { TwilioIconMinimal } from '../components/icons/TwilioIconMinimal';
import { ZendeskIcon } from '../components/icons/ZendeskIcon';
import { links as phoneLinks } from '../components/phone/links';
import {
  ServiceDesk as messages,
  Descriptions
} from '../components/service-desk/messages';
import {
  channelTypeToChannelName,
  getServiceDeskChannelName,
  AMAZON_CONNECT_BYOSD_TYPE,
  EGAIN_BYOSD_TYPE,
  ENGHOUSE_BYOSD_TYPE,
  GENESYS_BYOSD_TYPE,
  INTELEPEER_BYOSD_TYPE,
  NICE_INCONTACT_BYOSD_TYPE,
  KUSTOMER_BYOSD_TYPE,
  ORACLE_BYOSD_TYPE,
  PHONE_CHANNEL_TYPE,
  SALESFORCE_SERVICEDESK_TYPE,
  TWILIO_BYOSD_TYPE,
  WEBCHAT_CHANNEL_TYPE,
  WEB_BYOSD_TYPE,
  ZENDESK_SERVICEDESK_TYPE,
  INTELEPEER_SMS_TYPE,
  GENESYS_SMS_TYPE,
  TWILIO_SMS_TYPE,
  GENESYS_AUDIO_CONNECTOR_TYPE,
  CUSTOM_SMS_TYPE
} from '../util/channelTypes';
import { INTEGRATIONS_FEATURE_FLAGS } from '../util/integrationsUtil';
import { TWILIO_COLOR_PRIMARY } from '../util/validation/constants';
import { FormattedMessageWithLink } from './linkHelpers';

import '@ibm-watson/shared/modules/channels/components/service-desk/ServiceDeskCard.scss';

export const getLogoTitle = (serviceDeskType, intl) => {
  switch (serviceDeskType) {
    case AMAZON_CONNECT_BYOSD_TYPE:
      return `${
        channelTypeToChannelName[AMAZON_CONNECT_BYOSD_TYPE]
      }${intl.formatMessage(messages.logoLabel)}`;
    case TWILIO_BYOSD_TYPE:
      return `${
        channelTypeToChannelName[TWILIO_BYOSD_TYPE]
      }${intl.formatMessage(messages.logoLabel)}`;
    case GENESYS_BYOSD_TYPE:
      return `${
        channelTypeToChannelName[GENESYS_BYOSD_TYPE]
      }${intl.formatMessage(messages.logoLabel)}`;
    case NICE_INCONTACT_BYOSD_TYPE:
      return `${
        channelTypeToChannelName[NICE_INCONTACT_BYOSD_TYPE]
      }${intl.formatMessage(messages.logoLabel)}`;
    case ORACLE_BYOSD_TYPE:
      return `${
        channelTypeToChannelName[ORACLE_BYOSD_TYPE]
      }${intl.formatMessage(messages.logoLabel)}`;
    case KUSTOMER_BYOSD_TYPE:
      return `${
        channelTypeToChannelName[KUSTOMER_BYOSD_TYPE]
      }${intl.formatMessage(messages.logoLabel)}`;
    case INTELEPEER_BYOSD_TYPE:
      return `${
        channelTypeToChannelName[INTELEPEER_BYOSD_TYPE]
      }${intl.formatMessage(messages.logoLabel)}`;
    case SALESFORCE_SERVICEDESK_TYPE:
      return `${
        channelTypeToChannelName[SALESFORCE_SERVICEDESK_TYPE]
      }${intl.formatMessage(messages.logoLabel)}`;
    case ZENDESK_SERVICEDESK_TYPE:
      return `${
        channelTypeToChannelName[ZENDESK_SERVICEDESK_TYPE]
      }${intl.formatMessage(messages.logoLabel)}`;
    case EGAIN_BYOSD_TYPE:
      return `${channelTypeToChannelName[EGAIN_BYOSD_TYPE]}${intl.formatMessage(
        messages.logoLabel
      )}`;
    case ENGHOUSE_BYOSD_TYPE:
      return `${
        channelTypeToChannelName[ENGHOUSE_BYOSD_TYPE]
      }${intl.formatMessage(messages.logoLabel)}`;
    default:
      return `${channelTypeToChannelName[WEB_BYOSD_TYPE]}${intl.formatMessage(
        messages.logoLabel
      )}`;
  }
};

export const getServiceDeskDescription = (
  parentChannelType,
  serviceDeskType,
  t
) => {
  switch (serviceDeskType) {
    case ZENDESK_SERVICEDESK_TYPE:
      return `${t(Descriptions.zendesk)}`;
    case SALESFORCE_SERVICEDESK_TYPE:
      return `${t(Descriptions.salesforce)}`;
    case TWILIO_BYOSD_TYPE:
      return `${t(Descriptions.twilio)}`;
    case GENESYS_BYOSD_TYPE:
      return `${t(Descriptions.genesys)}`;
    case GENESYS_AUDIO_CONNECTOR_TYPE:
      return (
        <FormattedMessageWithLink
          url={phoneLinks.genesysAudioConnectorDocs}
          {...Descriptions.genesysAudioConnector}
        />
      );
    case NICE_INCONTACT_BYOSD_TYPE:
      if (parentChannelType === WEBCHAT_CHANNEL_TYPE) {
        return `${t(Descriptions.niceIncontact)}`;
      } else if (parentChannelType === PHONE_CHANNEL_TYPE) {
        return (
          <FormattedMessageWithLink
            url={phoneLinks.learnMoreNiceCXoneLink}
            {...Descriptions.niceIncontactPhone}
          />
        );
      }
      return;
    case AMAZON_CONNECT_BYOSD_TYPE:
      return (
        <FormattedMessageWithLink
          url={phoneLinks.awsSteps}
          {...Descriptions.amazonConnect}
        />
      );
    case ORACLE_BYOSD_TYPE:
      return `${t(Descriptions.oracle)}`;
    case KUSTOMER_BYOSD_TYPE:
      return `${t(Descriptions.kustomer)}`;
    case EGAIN_BYOSD_TYPE:
      return `${t(Descriptions.egain)}`;
    case ENGHOUSE_BYOSD_TYPE:
      return `${t(Descriptions.enghouse)}`;
    default:
      if (parentChannelType == WEBCHAT_CHANNEL_TYPE) {
        return `${t(Descriptions.byo)}`;
      }
      return;
  }
};

// The service desks that are available for web chat to extend with.
export const supportedWebChatServiceDesks = (features, plan) => {
  let enabledServiceDesks = [];
  // Set up which integrations will be allowed in dropdown by feature flag.
  // These should be in alpha order.
  if (plan !== PlanName.STANDARD || (plan === PlanName.STANDARD && isCpd)) {
    enabledServiceDesks = [
      EGAIN_BYOSD_TYPE,
      ENGHOUSE_BYOSD_TYPE,
      GENESYS_BYOSD_TYPE,
      KUSTOMER_BYOSD_TYPE,
      NICE_INCONTACT_BYOSD_TYPE,
      ORACLE_BYOSD_TYPE,
      ...(features[INTEGRATIONS_FEATURE_FLAGS.SALESFORCE]
        ? [SALESFORCE_SERVICEDESK_TYPE]
        : []),
      TWILIO_BYOSD_TYPE,
      ...(features[INTEGRATIONS_FEATURE_FLAGS.ZENDESK]
        ? [ZENDESK_SERVICEDESK_TYPE]
        : []),
      WEB_BYOSD_TYPE
    ];
  }
  return enabledServiceDesks;
};

// The service desks that are available for phone to extend with.
export const supportedPhoneServiceDesks = (
  isAmazonConnectEnabled,
  isNiceCXoneEnabled,
  isGenesysAudioConnectorEnabled,
  region,
  plan,
  isGenesysAudioConnectorEnabledInCpd
) => {
  // Currently only genesys audio connector is enabled in CPD.
  // No other integrations is supported
  if (isGenesysAudioConnectorEnabledInCpd) {
    return [GENESYS_AUDIO_CONNECTOR_TYPE];
  }

  const enabledServiceDesks = [
    GENESYS_BYOSD_TYPE,
    ...(isGenesysAudioConnectorEnabled ? [GENESYS_AUDIO_CONNECTOR_TYPE] : []),
    // Hide Intelepeer in WXO
    ...(!isWxoEnabled ? [INTELEPEER_BYOSD_TYPE] : []),
    ...(isNiceCXoneEnabled ? [NICE_INCONTACT_BYOSD_TYPE] : []),
    TWILIO_BYOSD_TYPE,
    WEB_BYOSD_TYPE
  ];

  if (isAmazonConnectEnabled) {
    enabledServiceDesks.unshift(AMAZON_CONNECT_BYOSD_TYPE);
  }

  return enabledServiceDesks;
};

interface ServiceDeskLogoProps {
  className?: string;
  minimal?: boolean;
  modal?: string;
  parentChannelType?: string;
  serviceDeskType: string;
  showTitle: boolean;
  size?: number;
  // hideLogo is the property used to control hiding logo in certain conditions
  hideLogo?: boolean;
}
/**
 * @param {bool} showTitle show service desk title
 * @returns {Component} contructed component with service desk logo
 */
export const ServiceDeskLogo = ({
  className,
  parentChannelType,
  serviceDeskType,
  modal,
  size,
  minimal,
  showTitle,
  hideLogo = true
}: ServiceDeskLogoProps) => {
  const intl = useIntl();
  const amazonConnectEnabled = useAppSelector(state =>
    isFeatureEnabled(state, INTEGRATIONS_FEATURE_FLAGS.PHONE_AMAZON_CONNECT)
  );

  const isGenesysAudioConnectorEnabledInCpd = useAppSelector(
    selectIsGenesysAudioConnectorInCpd
  );

  if (
    [
      WEB_BYOSD_TYPE,
      ORACLE_BYOSD_TYPE,
      KUSTOMER_BYOSD_TYPE,
      EGAIN_BYOSD_TYPE,
      ENGHOUSE_BYOSD_TYPE
    ].includes(serviceDeskType)
  ) {
    return (
      <div
        className={cx('ServiceDesk__logo-title-container', className, modal)}
        title={
          getLogoTitle(serviceDeskType, intl) ||
          `${intl.formatMessage(
            messages.generalHelpDeskLabel
          )}${intl.formatMessage(messages.logoLabel)}`
        }>
        {parentChannelType === WEBCHAT_CHANNEL_TYPE && (
          <HelpDesk
            size={24}
            className="ServiceDesk__logo"
            aria-label={getLogoTitle(serviceDeskType, intl)}
            aria-hidden="true"
            title={getLogoTitle(serviceDeskType, intl)}
          />
        )}
        {parentChannelType === PHONE_CHANNEL_TYPE && (
          <ManagedSolutions
            size={24}
            className="ServiceDesk__logo"
            aria-label={intl.formatMessage(messages.generalHelpDeskLabel)}
            aria-hidden="true"
            title={getLogoTitle(serviceDeskType, intl)}
          />
        )}
        {showTitle && (
          <h4 className="ServiceDesk__logo-title-other">
            {getServiceDeskChannelName(serviceDeskType, parentChannelType)}
          </h4>
        )}
      </div>
    );
  } else {
    if (
      (serviceDeskType === AMAZON_CONNECT_BYOSD_TYPE &&
        !amazonConnectEnabled) ||
      // Do not show an icon under Phone tile for Genesys Audio connector since only one
      // Genesys icon is needed to be shown.
      (serviceDeskType === GENESYS_AUDIO_CONNECTOR_TYPE &&
        hideLogo &&
        !isGenesysAudioConnectorEnabledInCpd)
    ) {
      return <></>;
    } else {
      return (
        // Below is to render "Contact center integrations" aka "Supported live agent integrations"
        // Icons for Phone tile. Minimal icons are for Tile and full size once are rendered in Model
        <div
          className={cx('ServiceDesk__logo-title-container', className, modal)}>
          {serviceDeskType === TWILIO_BYOSD_TYPE &&
            (minimal ? (
              <TwilioIconMinimal
                className={`ServiceDesk--${serviceDeskType}`}
                size={size}
                fill={TWILIO_COLOR_PRIMARY}
              />
            ) : (
              <TwilioIcon
                className={`ServiceDesk--${serviceDeskType}`}
                channelType={parentChannelType}
                size={size}
                fill={TWILIO_COLOR_PRIMARY}
              />
            ))}
          {(serviceDeskType === GENESYS_BYOSD_TYPE ||
            serviceDeskType === GENESYS_AUDIO_CONNECTOR_TYPE ||
            serviceDeskType === GENESYS_SMS_TYPE) &&
            (minimal ? (
              <GenesysIconMinimal
                className={`ServiceDesk--${serviceDeskType}`}
                size={size}
              />
            ) : (
              <GenesysIcon
                className={`ServiceDesk--${serviceDeskType}`}
                size={size}
              />
            ))}
          {serviceDeskType === ZENDESK_SERVICEDESK_TYPE && (
            <ZendeskIcon
              className={`ServiceDesk--${serviceDeskType}`}
              size={size}
            />
          )}
          {serviceDeskType === SALESFORCE_SERVICEDESK_TYPE && (
            <SalesforceIcon
              className={`ServiceDesk--${serviceDeskType}`}
              size={size}
            />
          )}
          {serviceDeskType === INTELEPEER_BYOSD_TYPE &&
            (minimal ? (
              <IntelepeerIconMinimal
                className={`ServiceDesk--${serviceDeskType}`}
                size={size}
              />
            ) : (
              <IntelepeerIcon
                className={`ServiceDesk--${serviceDeskType}`}
                size={size}
              />
            ))}
          {serviceDeskType === NICE_INCONTACT_BYOSD_TYPE &&
            (minimal ? (
              <NiceInContactIconMinimal
                className={`ServiceDesk--${serviceDeskType}`}
                size={size}
              />
            ) : (
              <NiceInContactIcon
                className={`ServiceDesk--${serviceDeskType}`}
                size={24}
              />
            ))}
          {serviceDeskType === AMAZON_CONNECT_BYOSD_TYPE &&
            (minimal ? (
              <AmazonConnectIconMinimal
                className={`ServiceDesk--${serviceDeskType}`}
                size={size}
              />
            ) : (
              <AmazonConnectIcon
                className={`ServiceDesk--${serviceDeskType}`}
                size={40}
              />
            ))}
          {serviceDeskType === TWILIO_SMS_TYPE &&
            (minimal ? (
              <TwilioIconMinimal
                className={`ServiceDesk--${serviceDeskType}`}
                size={size}
                fill={TWILIO_COLOR_PRIMARY}
              />
            ) : (
              <TwilioIcon
                className={`ServiceDesk--${serviceDeskType}`}
                channelType={parentChannelType}
                size={size}
                fill={TWILIO_COLOR_PRIMARY}
              />
            ))}
          {serviceDeskType === INTELEPEER_SMS_TYPE && (
            <IntelepeerIcon
              className={`ServiceDesk--${serviceDeskType}-sms`}
              size={size}
            />
          )}
          {serviceDeskType === CUSTOM_SMS_TYPE && <CustomSMSIcon />}
          {showTitle && serviceDeskType !== GENESYS_BYOSD_TYPE && (
            <h4 className="ServiceDesk__logo-title">
              {getServiceDeskChannelName(serviceDeskType, parentChannelType)}
            </h4>
          )}
        </div>
      );
    }
  }
};
